import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { wfsCache, debounce, bboxOverlap, requestManager } from '../../utils/wfsCache';
import {
    createSingleAssayPopupTable,
    SOIL_MAP_LAYER,
    SOIL_TILE_SOURCE,
    SOIL_TILE_SOURCE_LAYER,
    SOIL_TILE_BACKGROUND_LAYER,
    soilTileUrl,
    buildSoilTileFilter,
    SOIL_HEAT_SOURCE,
    SOIL_POINT_MINZOOM,
    soilHeatTileUrl,
    soilHeatmapPaintFor,
    buildSoilHeatFilter,
} from './AssayOverlay';
import { trackWfsQuery, timed } from '../../firebase/telemetry';
import { logError } from '../../firebase/errorReporting';

import {
    Popup,
    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';

const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER,
    timeout: 30000
});

// Debounced query function
export const debouncedQueryAssayWFS = debounce(queryAssayWFSOptimized, 300);

/**
 * Build a CQL clause filtering on gswa_load_date (an xsd:dateTime attribute in
 * GeoServer) from the month/year range in assayFilters.
 *
 * dateFrom / dateTo are 'YYYY-MM' month strings. The range is inclusive of both
 * selected months: the lower bound is the first instant of dateFrom's month and
 * the upper bound is the first instant of the month AFTER dateTo (exclusive), so
 * the whole of dateTo's month is covered.
 *
 * Samples with a null gswa_load_date are always included while the filter is
 * active, so enabling the filter never silently drops undated samples.
 *
 * Returns '' (no clause) when the filter is disabled or no bound is set.
 */
function buildLoadDateClause(assayFilters) {
    if (!assayFilters.dateFilterEnabled) return '';

    const { dateFrom, dateTo } = assayFilters;
    if (!dateFrom && !dateTo) return '';

    // 'YYYY-MM' -> Date at the first instant (UTC) of that month, optionally
    // advancing by `monthOffset` whole months first. GeoServer's ECQL temporal
    // parser only accepts yyyy-MM-ddTHH:mm:ssZ, so strip the .SSS milliseconds
    // that Date.toISOString() emits (a fractional second makes the whole clause
    // fail to parse and the WFS query error out).
    const monthStartIso = (ym, monthOffset = 0) => {
        const [y, m] = ym.split('-').map(Number);
        // m is 1-based; Date month arg is 0-based, so (m - 1) + offset.
        return new Date(Date.UTC(y, (m - 1) + monthOffset, 1))
            .toISOString().replace(/\.\d{3}Z$/, 'Z');
    };

    const conditions = [];
    if (dateFrom) conditions.push(`gswa_load_date >= ${monthStartIso(dateFrom)}`);
    if (dateTo) conditions.push(`gswa_load_date < ${monthStartIso(dateTo, 1)}`);

    // Wrap the range so nulls can be OR'd in without being consumed by the
    // outer AND that joins this clause to the rest of the filter.
    return `and (( ${conditions.join(' and ')} ) or gswa_load_date is null)`;
}

/**
 * Optimized assay WFS query with caching and request management
 */
export function queryAssayWFSOptimized(bboxString, setStateGeoJSON, assayFilters, retryCount = 0) {
    if (assayFilters.anumber !== 0) return Promise.resolve();

    const cacheKey = wfsCache.generateKey(bboxString, assayFilters, 'assays');
    const startedAt = Date.now();

    // Check cache first
    const cachedResult = wfsCache.get(cacheKey);
    if (cachedResult) {
        // Only report a hit on the first attempt: a retry that lands on a
        // cache entry would otherwise inflate the hit rate.
        if (retryCount === 0) {
            trackWfsQuery({
                layer: 'assays',
                cacheHit: true,
                durationMs: Date.now() - startedAt,
                featureCount: cachedResult?.features?.length,
            });
        }
        setStateGeoJSON(cachedResult);
        return Promise.resolve(cachedResult);
    }

    // Create abort controller for this request
    const controller = requestManager.createController('assays');

    const assayTypeArr = assayFilters.type.map(e => e.value);

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and
        (
        au_ppb BETWEEN ${assayFilters.ppbmt} AND ${assayFilters.ppblt}
        ${assayFilters.nullValues ? 'OR au_ppb is null' : ''}
        )
        and
        surfacesampletype in ('${assayTypeArr.join(`','`)}')
        ${buildLoadDateClause(assayFilters)}
    `;

    return timed('wfs_assays', () => GoldFinderGeoserver.get("/ne/ows?", {
        signal: controller.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: SOIL_MAP_LAYER,
            maxFeatures: 99999,
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    })).then((response) => {
        trackWfsQuery({
            layer: 'assays',
            cacheHit: false,
            durationMs: Date.now() - startedAt,
            featureCount: response.data?.features?.length,
        });
        // Cache the result
        wfsCache.set(cacheKey, response.data);
        setStateGeoJSON(response.data);
        return response.data;
    }).catch((error) => {
        const canceled = error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED';
        if (!canceled) {
            console.error('Assay WFS query error:', error);
            // Cancellations are routine (every map pan aborts in-flight
            // queries) — reporting them would drown the real failures.
            trackWfsQuery({
                layer: 'assays',
                cacheHit: false,
                durationMs: Date.now() - startedAt,
                featureCount: 0,
                status: error.code === 'ECONNABORTED' ? 'timeout' : 'error',
            });
        }

        // Don't re-throw canceled errors
        if (canceled) {
            return null;
        }

        // Retry on timeout or network errors
        const maxRetries = 10;
        const baseDelay = 1000;
        if ((error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') && retryCount < maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retrying assay query in ${delay}ms (attempt ${retryCount + 1}/${maxRetries + 1})`);
            
            return new Promise(resolve => {
                setTimeout(() => {
                    resolve(queryAssayWFSOptimized(bboxString, setStateGeoJSON, assayFilters, retryCount + 1));
                }, delay);
            });
        }
        
        // For other errors or max retries exceeded, return empty GeoJSON to keep UI functional.
        // The UI silently shows zero samples here, which is indistinguishable
        // from an area that genuinely has none — so this must be reported.
        logError(error, { component: 'AssayOverlay', layer: 'assays', retryCount });

        const emptyResult = {
            type: "FeatureCollection",
            features: []
        };
        
        // Set empty result in state to prevent UI breakage
        setStateGeoJSON(emptyResult);
        
        // Return empty result instead of throwing error
        return emptyResult;
    });
}

/**
 * Optimized AssayLayers component with memoization
 */
function AssayLayersOptimized({ assayMarkerColourSettings, overlayVisibility, assayFilters }) {
    const userFilter = useMemo(() => buildSoilTileFilter(assayFilters), [assayFilters]);
    const orderedAssayMarkerArray = useMemo(() => 
        assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan),
        [assayMarkerColourSettings]
    );

    const layers = useMemo(() => {
        const layerConfigs = [];

        for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
            const next = i + 1;
            const config = orderedAssayMarkerArray[i];

            const upper = i !== orderedAssayMarkerArray.length - 1
                ? orderedAssayMarkerArray[next].moreThan
                : 9999;

            const layerConfig = {
                id: config.id,
                source: SOIL_TILE_SOURCE,
                'source-layer': SOIL_TILE_SOURCE_LAYER,
                type: 'circle',
                minzoom: SOIL_POINT_MINZOOM,
                paint: { 
                    'circle-color': config.color, 
                    'circle-radius': 6,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff'
                },
                // The colour band, AND the user's filters - the server no
                // longer applies them, because a tile is the same for
                // everybody. PPB is converted because the tile carries au_ppb
                // as a string; comparing it raw sorts "100" before "30".
                filter: ['all',
                    userFilter,
                    ['>=', ['to-number', ['get', 'au_ppb']], config.moreThan],
                    ['<',  ['to-number', ['get', 'au_ppb']], upper]
                ],
                layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
            };

            layerConfigs.push(layerConfig);
        }

        return layerConfigs;
    }, [orderedAssayMarkerArray, overlayVisibility.soils, userFilter]);

    return (
        <>
            {layers.map((layerConfig) => (
                <Layer key={layerConfig.id} {...layerConfig} />
            ))}
        </>
    );
}

/**
 * Optimized AssayOverlay component
 */
export function AssayOverlayMapLibreOptimized({ 
    overlayVisibility, 
    assayMarkerColourSettings, 
    assayGeoJson, 
    setAssayGeojson, 
    assayFilters 
}) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "assay_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap();
    const lastBboxRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    // Memoize background layer config
    // Click target and halo, under the coloured bands. This is the only soil
    // layer registered in interactiveLayerIds, so a click anywhere on a sample
    // resolves here rather than depending on which colour band it fell into.
    const backgroundConfig = useMemo(() => ({
        id: SOIL_TILE_BACKGROUND_LAYER,
        source: SOIL_TILE_SOURCE,
        'source-layer': SOIL_TILE_SOURCE_LAYER,
        minzoom: SOIL_POINT_MINZOOM,
        type: 'circle',
        paint: { 
            'circle-color': '#f9f9f9', 
            'circle-radius': 7,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#e0e0e0'
        },
        // Must carry the same user filter as the bands above it, or filtered-out
        // samples keep a white dot and stay clickable.
        filter: buildSoilTileFilter(assayFilters),
        layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
    }), [overlayVisibility.soils, assayFilters]);

    // A-Number mode still comes over WFS as GeoJSON: it is a whole-of-report
    // selection with no bbox, so tiles - which only hold what is in view -
    // cannot answer it. The two are mutually exclusive.
    const anumberMode = assayFilters.anumber !== 0;

    const anumberLayerConfig = useMemo(() => ({
        id: 'assay-anumber-config',
        source: 'soilsamples',
        type: 'circle',
        paint: {
            'circle-color': '#800080',
            'circle-radius': 6,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff'
        },
        layout: { visibility: `${overlayVisibility.soils && anumberMode ? "visible" : "none"}` }
    }), [overlayVisibility.soils, anumberMode]);

    // Optimized query function
    const queryAssays = useCallback(async (bbox) => {
        if (!goldfindermap || goldfindermap.getZoom() <= 10.6) return;

        // Check if bbox has changed significantly
        if (lastBboxRef.current && bboxOverlap(bbox, lastBboxRef.current)) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await debouncedQueryAssayWFS(bbox, setAssayGeojson, assayFilters);
            if (result !== null) {
                lastBboxRef.current = bbox;
            }
        } catch (error) {
            if (error.name !== 'AbortError' && error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                console.error('Error querying assays:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [goldfindermap, assayFilters, setAssayGeojson]);

    // Effect for filter changes
    useEffect(() => {
        if (!goldfindermap) return;
        
        // Clear cache when filters change
        wfsCache.clearLayer('assays');
        // The bbox guard in the query above is a VIEWPORT dedupe and knows
        // nothing about filters: it skips any fetch whose bbox overlaps the
        // last one by more than 80%. On a filter change the bbox is identical,
        // so it overlaps 100% and the fetch was skipped every time - the map
        // kept showing the old filter's results until you panned far enough to
        // beat the threshold. Forgetting the last bbox is the same statement
        // as clearing the cache on the line above: what we fetched before was
        // for different filters and does not count.
        lastBboxRef.current = null;
        
        if (goldfindermap.getZoom() > 10.6) {
            const bounds = goldfindermap.getBounds();
            const wfsbbox = [
                bounds.getSouthWest().lng, 
                bounds.getSouthWest().lat, 
                bounds.getNorthEast().lng, 
                bounds.getNorthEast().lat
            ].join();
            
            queryAssays(wfsbbox);
        }
    }, [assayFilters, queryAssays]);

    // Zoomed out, soils are drawn as a heatmap off a pre-aggregated grid.
    //
    // Every circle layer here is gated on minzoom 10.6, so below that the soil
    // layer has always simply been empty - 7.26M points cannot be fetched or
    // drawn at state scale. The grid answers a different question (how much is
    // here, how hot) that every sample contributes to, in tiles of ~266 KB.
    const heatConfig = useMemo(() => ({
        id: 'soil-heat-layer',
        source: SOIL_HEAT_SOURCE,
        'source-layer': 'wa_au_soil_assays_heat',
        type: 'heatmap',
        // The opacity ramp reaches zero at exactly SOIL_POINT_MINZOOM, so the
        // layer can stop there too - MapLibre hides a layer at zoom >= maxzoom
        // and shows one at zoom >= minzoom, making this and the point layers
        // exact complements. No padding needed: nothing is cut off mid-fade
        // because the fade has already finished.
        maxzoom: SOIL_POINT_MINZOOM,
        // Same filters as the dots. The grid carries a max per sample type and
        // the load-date bounds per cell precisely so this is possible - without
        // it, raising the threshold thinned the points and left the heat
        // untouched, and the heat is what covers the zoomed-out view.
        filter: buildSoilHeatFilter(assayFilters),
        paint: soilHeatmapPaintFor(assayFilters),
        layout: { visibility: `${overlayVisibility.soils ? "visible" : "none"}` }
    }), [overlayVisibility.soils, assayFilters]);

    return (
        <>
            {/*
              * Tiles come straight from GWC, so unlike the WFS points this
              * needs no bbox tracking, no debounce and no abort controller -
              * MapLibre requests what the viewport covers and caches it. The
              * grid is pre-aggregated, so panning at low zoom costs the
              * database nothing.
              */}
            <Source id={SOIL_HEAT_SOURCE} type="vector" scheme="tms"
                    tiles={[soilHeatTileUrl]} minzoom={1} maxzoom={11}>
                {/*
                  * Pinned below anchor-overlay-top, so the heatmap sits above
                  * the base imagery but UNDER every vector overlay. Without
                  * this it appends on mount and lands on top of tenements,
                  * which are drawn from z8 - the two bands overlap between z8
                  * and 10.6, and a wash at up to 0.85 opacity over tenement
                  * fills and outlines reads as the tenements having vanished.
                  */}
                <Layer {...heatConfig} beforeId='anchor-overlay-top' />
            </Source>
            {/*
              * Samples as vector tiles. GWC and the browser cache them, so
              * panning re-fetches almost nothing and the filters are applied
              * client-side against the attributes the tile carries.
              */}
            <Source id={SOIL_TILE_SOURCE} type="vector" scheme="tms"
                    tiles={[soilTileUrl]} minzoom={7} maxzoom={16}>
                <Layer {...backgroundConfig} />
                <AssayLayersOptimized 
                    overlayVisibility={overlayVisibility} 
                    assayMarkerColourSettings={assayMarkerColourSettings}
                    assayFilters={assayFilters}
                />
            </Source>
            {/* A-Number results, and the source the KML export reads. */}
            <Source id="soilsamples" type="geojson" data={assayGeoJson}>
                <Layer {...anumberLayerConfig} />
            </Source>
        </>
    );
}